import React, { useState, useEffect, useCallback } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  ColumnDef,
  ColumnOrderState,
  SortingState,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Typography,
  TableSortLabel,
  Chip,
} from '@mui/material';
import {
  DragIndicator as DragIndicatorIcon,
  SentimentDissatisfied as EmptyIcon,
} from '@mui/icons-material';
import { FilterContent } from './filters/FilterContent';
import { DateFilterContent } from './filters/DateFilterContent';

export interface SpreadsheetProps<T extends { id: string }> {
  // Data
  data: T[];
  columns: ColumnDef<T>[];
  
  // Callbacks
  onRowUpdate?: (rowId: string, field: keyof T, value: any) => void;
  onSelectionChange?: (selectedIds: Set<string>) => void;
  
  // Filtering
  enableFiltering?: boolean;
  filterableColumns?: string[];
  uniqueValues?: Record<string, string[]>;
  columnFilters?: Record<string, string[]>;
  onColumnFiltersChange?: (filters: Record<string, string[]>) => void;
  
  // Date filtering
  enableDateFiltering?: boolean;
  selectedYears?: string[];
  selectedMonths?: string[];
  onYearSelectionChange?: (years: string[]) => void;
  onMonthSelectionChange?: (months: string[]) => void;
  availableYears?: string[];
  availableMonths?: string[];
  
  // Sorting
  manualSorting?: boolean;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState | ((old: SortingState) => SortingState)) => void;
  
  // Column ordering
  enableColumnReordering?: boolean;
  columnOrder?: ColumnOrderState;
  onColumnOrderChange?: (order: ColumnOrderState | ((old: ColumnOrderState) => ColumnOrderState)) => void;
  
  // Selection
  enableSelection?: boolean;
  selectedRows?: Set<string>;
  
  // Infinite scroll
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  
  // Styling
  className?: string;
  emptyMessage?: string;
  hideStatusBar?: boolean;
  hideLoadMoreIndicator?: boolean;
}

export function Spreadsheet<T extends { id: string }>({
  data,
  columns,
  onRowUpdate,
  onSelectionChange,
  enableFiltering = false,
  filterableColumns = [],
  uniqueValues = {},
  columnFilters = {},
  onColumnFiltersChange,
  enableDateFiltering = false,
  selectedYears = [],
  selectedMonths = [],
  onYearSelectionChange,
  onMonthSelectionChange,
  availableYears = [],
  availableMonths = [],
  manualSorting = false,
  sorting = [],
  onSortingChange,
  enableColumnReordering = false,
  columnOrder,
  onColumnOrderChange,
  enableSelection = false,
  selectedRows = new Set<string>(),
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  className = '',
  emptyMessage = 'No data available',
  hideStatusBar = false,
  hideLoadMoreIndicator = false,
}: SpreadsheetProps<T>) {
  const [internalSorting, setInternalSorting] = useState<SortingState>(sorting);
  const [internalColumnOrder, setInternalColumnOrder] = useState<ColumnOrderState>(columnOrder || []);
  const [openFilterColumn, setOpenFilterColumn] = useState<string | null>(null);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const columnDraggingEnabled = enableColumnReordering;

  // Intersection observer for infinite scroll
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    rootMargin: '100px',
  });

  // Load more when the sentinel comes into view
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && onLoadMore) {
      onLoadMore();
    }
  }, [inView, hasNextPage, isFetchingNextPage, onLoadMore]);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-filter-dropdown]') && !target.closest('[data-filter-trigger]')) {
        setOpenFilterColumn(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const table = useReactTable({
    data,
    columns,
    getRowId: (row) => row.id,
    state: {
      sorting: onSortingChange ? sorting : internalSorting,
      columnOrder: onColumnOrderChange ? columnOrder : internalColumnOrder,
    },
    onSortingChange: (updaterOrValue) => {
      if (onSortingChange) {
        onSortingChange(updaterOrValue);
      } else {
        setInternalSorting(updaterOrValue);
      }
    },
    onColumnOrderChange: (updaterOrValue) => {
      if (onColumnOrderChange) {
        onColumnOrderChange(updaterOrValue);
      } else {
        setInternalColumnOrder(updaterOrValue);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting,
  });

  const handleDragStart = (e: React.DragEvent, columnId: string) => {
    setDraggedColumn(columnId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn === targetColumnId) return;

    const currentOrder = table.getAllColumns().map(col => col.id);
    const draggedIndex = currentOrder.indexOf(draggedColumn);
    const targetIndex = currentOrder.indexOf(targetColumnId);

    const newOrder = [...currentOrder];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedColumn);

    if (onColumnOrderChange) {
      onColumnOrderChange(newOrder);
    } else {
      setInternalColumnOrder(newOrder);
    }
  };

  const handleDragEnd = useCallback(() => {
    setDraggedColumn(null);
  }, []);

  const handleFilterClick = useCallback((columnId: string) => {
    if (!enableFiltering || !filterableColumns.includes(columnId)) return;
    setOpenFilterColumn(prev => prev === columnId ? null : columnId);
  }, [enableFiltering, filterableColumns]);

  const handleCloseFilter = useCallback(() => {
    setOpenFilterColumn(null);
  }, []);

  return (
    <Paper elevation={2} sx={{ overflow: 'hidden', width: '100%', maxWidth: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <TableContainer sx={{ flex: 1, overflowX: 'auto', width: '100%', maxWidth: '100%' }}>
        <Table stickyHeader sx={{ minWidth: 650 }}>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const isFilterable = enableFiltering && filterableColumns.includes(header.id);
                  const isSortable = header.column.getCanSort();
                  const sortDirection = header.column.getIsSorted();
                  
                  return (
                    <TableCell
                      key={header.id}
                      sx={{
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        fontSize: '0.75rem',
                        letterSpacing: '0.05em',
                        backgroundColor: draggedColumn === header.id ? 'primary.light' : 'grey.50',
                        cursor: columnDraggingEnabled && header.id !== 'select' ? 'move' : 'default',
                        '&:hover': columnDraggingEnabled && header.id !== 'select' ? {
                          backgroundColor: 'grey.100',
                        } : {},
                        width: header.getSize(),
                        minWidth: header.column.columnDef.minSize,
                        maxWidth: header.column.columnDef.maxSize,
                        position: 'relative',
                      }}
                      draggable={columnDraggingEnabled && header.id !== 'select'}
                      onDragStart={columnDraggingEnabled && header.id !== 'select' ? (e) => handleDragStart(e, header.id) : undefined}
                      onDragOver={columnDraggingEnabled && header.id !== 'select' ? handleDragOver : undefined}
                      onDrop={columnDraggingEnabled && header.id !== 'select' ? (e) => handleDrop(e, header.id) : undefined}
                      onDragEnd={columnDraggingEnabled && header.id !== 'select' ? handleDragEnd : undefined}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                          {columnDraggingEnabled && header.id !== 'select' && (
                            <DragIndicatorIcon 
                              sx={{ fontSize: '1rem', color: 'text.secondary', cursor: 'move' }} 
                              titleAccess="Drag to reorder" 
                            />
                          )}
                          
                          {/* Column header with sorting and/or filtering */}
                          {isSortable && isFilterable ? (
                            /* Both sortable and filterable - split into two clickable areas */
                            <>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  flex: 1,
                                  cursor: 'pointer',
                                  '&:hover': {
                                    backgroundColor: 'grey.100',
                                    borderRadius: 1,
                                  },
                                  px: 1,
                                  py: 0.5,
                                }}
                                onClick={() => handleFilterClick(header.id)}
                                data-filter-trigger={true}
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </Box>
                              <TableSortLabel
                                active={!!sortDirection}
                                direction={sortDirection === 'asc' ? 'asc' : 'desc'}
                                onClick={header.column.getToggleSortingHandler()}
                                sx={{
                                  '& .MuiTableSortLabel-icon': {
                                    opacity: sortDirection ? 1 : 0.3,
                                  },
                                  '&:hover .MuiTableSortLabel-icon': {
                                    opacity: 1,
                                  },
                                }}
                              />
                            </>
                          ) : isSortable ? (
                            /* Only sortable */
                            <TableSortLabel
                              active={!!sortDirection}
                              direction={sortDirection === 'asc' ? 'asc' : 'desc'}
                              onClick={header.column.getToggleSortingHandler()}
                              sx={{
                                flex: 1,
                                justifyContent: 'flex-start',
                                '& .MuiTableSortLabel-icon': {
                                  opacity: sortDirection ? 1 : 0.3,
                                },
                                '&:hover .MuiTableSortLabel-icon': {
                                  opacity: 1,
                                },
                              }}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </TableSortLabel>
                          ) : isFilterable ? (
                            /* Only filterable */
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flex: 1,
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: 'grey.100',
                                  borderRadius: 1,
                                },
                                px: 1,
                                py: 0.5,
                              }}
                              onClick={() => handleFilterClick(header.id)}
                              data-filter-trigger={true}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </Box>
                          ) : (
                            /* Neither sortable nor filterable */
                            <Box sx={{ flex: 1 }}>
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </Box>
                          )}
                        </Box>

                        {/* Filter dropdown */}
                        {isFilterable && openFilterColumn === header.id && (
                          <Paper
                            elevation={8}
                            sx={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              zIndex: 1300,
                              mt: 0.5,
                              width: 288,
                            }}
                            data-filter-dropdown
                          >
                            {header.id === 'date' && enableDateFiltering ? (
                              <DateFilterContent
                                title="Date"
                                selectedYears={selectedYears}
                                selectedMonths={selectedMonths}
                                onYearSelectionChange={onYearSelectionChange || (() => {})}
                                onMonthSelectionChange={onMonthSelectionChange || (() => {})}
                                availableYears={availableYears}
                                availableMonths={availableMonths}
                                onClose={handleCloseFilter}
                              />
                            ) : (
                              <FilterContent
                                title={String(header.column.columnDef.header)}
                                options={uniqueValues[header.id] || []}
                                selectedValues={columnFilters[header.id] || []}
                                onSelectionChange={(values) => {
                                  if (onColumnFiltersChange) {
                                    onColumnFiltersChange({ ...columnFilters, [header.id]: values });
                                  }
                                }}
                                onClose={handleCloseFilter}
                              />
                            )}
                          </Paper>
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={table.getAllColumns().length} sx={{ py: 12, textAlign: 'center' }}>
                  <Box sx={{ color: 'text.secondary' }}>
                    <EmptyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="body1" color="text.primary" fontWeight={500}>
                      {emptyMessage}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => {
                const isSelected = enableSelection && selectedRows.has(row.original.id);
                
                return (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      backgroundColor: isSelected ? 'primary.lighter' : 'inherit',
                      borderLeft: isSelected ? 4 : 0,
                      borderLeftColor: isSelected ? 'primary.main' : 'transparent',
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        sx={{
                          width: cell.column.getSize(),
                          minWidth: cell.column.columnDef.minSize,
                          maxWidth: cell.column.columnDef.maxSize,
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Load More Trigger */}
      {hasNextPage && (
        <Box
          ref={loadMoreRef}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: hideLoadMoreIndicator ? 0 : 2,
            minHeight: hideLoadMoreIndicator ? '20px' : 'auto',
            height: hideLoadMoreIndicator ? '20px' : 'auto',
            borderTop: hideLoadMoreIndicator ? 0 : 1,
            borderColor: 'divider',
            opacity: hideLoadMoreIndicator ? 0 : 1,
            pointerEvents: hideLoadMoreIndicator ? 'none' : 'auto',
            position: 'relative',
          }}
        >
          {!hideLoadMoreIndicator && (
            <>
              {isFetchingNextPage ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading more...
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Scroll to load more...
                </Typography>
              )}
            </>
          )}
        </Box>
      )}

      {/* Status Bar */}
      {data.length > 0 && !hideStatusBar && (
        <Box
          sx={{
            backgroundColor: 'grey.50',
            px: 2,
            py: 1,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {data.length} {data.length === 1 ? 'row' : 'rows'}
            {hasNextPage && ' (scroll for more)'}
          </Typography>
          {enableSelection && selectedRows.size > 0 && (
            <Chip 
              label={`${selectedRows.size} selected`} 
              size="small" 
              color="primary" 
              variant="outlined"
            />
          )}
        </Box>
      )}
    </Paper>
  );
}

